/**
 * The AI View's saved widgets and its most recent answers.
 *
 * A widget stores the *question*, not the answer. Coming back tomorrow re-asks
 * it, so a widget shows today's numbers rather than a screenshot of yesterday's
 * — which is the difference between a dashboard and a souvenir.
 *
 * Recent blocks are kept separately and do hold their answer: they are the tail
 * of what you were just doing, and re-running them on every visit would be a
 * surprise and a bill.
 *
 * Stored per browser. That is enough to stop the view emptying itself when you
 * navigate away, which was the complaint; syncing across devices needs a table
 * and is worth doing when there is no demo in the morning.
 */

const WIDGETS_KEY = 'hygge.ai.widgets.v1';
const BLOCKS_KEY = 'hygge.ai.blocks.v1';
/** Enough to be useful on return, small enough not to bloat localStorage. */
const MAX_BLOCKS = 6;

export interface Widget {
  id: string;
  /** What to ask again each time the view opens. */
  prompt: string;
  /** Short label for the widget header. */
  title: string;
  createdAt: number;
}

export interface StoredBlock {
  id: number;
  question: string;
  /** The reply as it was, so returning does not re-run and re-bill it. */
  reply: unknown;
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    // A corrupt or unavailable store must never take the page down with it.
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota or private mode: losing persistence is not worth an error.
  }
}

export function loadWidgets(): Widget[] {
  return read<Widget[]>(WIDGETS_KEY, []);
}

/** Adds a widget, ignoring one that asks exactly what an existing one asks. */
export function addWidget(prompt: string, title: string): Widget[] {
  const widgets = loadWidgets();
  const trimmed = prompt.trim();
  if (widgets.some((w) => w.prompt.trim().toLowerCase() === trimmed.toLowerCase())) return widgets;

  const next = [
    ...widgets,
    {
      id: `w${Date.now()}${Math.random().toString(36).slice(2, 6)}`,
      prompt: trimmed,
      title: title.trim() || trimmed,
      createdAt: Date.now(),
    },
  ];
  write(WIDGETS_KEY, next);
  return next;
}

export function removeWidget(id: string): Widget[] {
  const next = loadWidgets().filter((w) => w.id !== id);
  write(WIDGETS_KEY, next);
  return next;
}

/** Empties the board in one go, for rebuilding a dashboard from scratch. */
export function clearWidgets(): Widget[] {
  write(WIDGETS_KEY, []);
  return [];
}

export function loadBlocks(): StoredBlock[] {
  return read<StoredBlock[]>(BLOCKS_KEY, []);
}

export function saveBlocks(blocks: StoredBlock[]): void {
  write(BLOCKS_KEY, blocks.slice(0, MAX_BLOCKS));
}
